/**
 * AkountFly - Display Mode Context (Sprint 1 refactor)
 * =====================================================
 *
 * AkountFly ships a SINGLE unified UI - there is no Business/Accountant
 * toggle. This context still exists because:
 *   (a) Existing pages call `useDisplayMode().getLabel(...)` for canonical
 *       labels - we keep that surface so we have one source of truth.
 *   (b) The deprecated `useUserMode` / `useAccountingMode` hooks shim
 *       through this context to avoid touching the few orphan consumers
 *       that still import them.
 *
 * `toggleMode` is a no-op. `mode` is always `business`. Any future need
 * to re-introduce a mode toggle should be a deliberate product decision.
 */
import React, { createContext, useContext, useCallback } from 'react';
import {
  DISPLAY_MODES,
  LABELS,
  ACCOUNTANT_ONLY_FEATURES,
  MODE_REPORTS,
  REPORTS_LIST,
  GLOSSARY,
} from './displayLabels';

const DisplayModeContext = createContext(null);

export const DisplayModeProvider = ({ children }) => {
  // Hard-locked to the unified view. Kept as a const so any caller reading
  // `mode` gets a stable value.
  const mode = DISPLAY_MODES.BUSINESS;
  const isBusinessMode = true;
  const isAccountantMode = false;

  const noopToggle = useCallback(() => { /* unified view: no toggle */ }, []);

  // Canonical label resolver. Returns the single unified label for the
  // given key, falling back to the key itself if no mapping exists.
  const getLabel = useCallback((canonicalKey) => {
    const entry = LABELS[canonicalKey];
    if (!entry) return canonicalKey;
    // displayLabels.js now stores plain strings; fall back gracefully if a
    // legacy object-shape ({business, accountant}) leaks in from old code.
    if (typeof entry === 'string') return entry;
    return entry.business || entry.accountant || canonicalKey;
  }, []);

  // Backwards-compat helper - returns the same label (kept so existing
  // tooltip code referencing it doesn't crash; can be safely removed once
  // every consumer is migrated).
  const getAlternateLabel = useCallback((canonicalKey) => getLabel(canonicalKey), [getLabel]);

  // Unified view: every feature is visible to every user.
  const isFeatureVisible = useCallback((_featureKey) => true, []);

  const getAvailableReports = useCallback(() => REPORTS_LIST, []);

  const getGlossary = useCallback((canonicalKey) => GLOSSARY[canonicalKey] || null, []);

  const value = {
    mode,
    isBusinessMode,
    isAccountantMode,
    toggleMode: noopToggle,
    switchToMode: noopToggle,
    setMode: noopToggle,
    getLabel,
    getAlternateLabel,
    isFeatureVisible,
    getAvailableReports,
    getGlossary,
    DISPLAY_MODES,
    LABELS,
    ACCOUNTANT_ONLY_FEATURES,
    MODE_REPORTS,
    GLOSSARY,
  };

  return (
    <DisplayModeContext.Provider value={value}>
      {children}
    </DisplayModeContext.Provider>
  );
};

export const useDisplayMode = () => {
  const ctx = useContext(DisplayModeContext);
  if (!ctx) {
    throw new Error('useDisplayMode must be used within a DisplayModeProvider');
  }
  return ctx;
};

export default DisplayModeContext;
